/**
 * v1.5 SMS 配置（ControlPanel 📱 短信配置 tab）
 *
 * 仿 WeworkArchive 结构：表单 + 测试发送 + 最近日志
 * 后端：/admin/sms-config/{status, config, test-send}
 */
import { useState } from 'react';
import { Input, message, Tag, Modal } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { Btn, Pill } from '../../components/ui';
import { fmtMonthDayTime } from '../../lib/format';

type Status = {
  ak: string; sk: string; sign_name: string;
  template_verify: string; template_alert: string;
  sources: Record<string, 'kv' | 'env' | 'empty'>;
  configured: boolean;
  recent_logs: Array<{ at: string; phone: string; ok: boolean; msg: string; requestId?: string }>;
};

export function SmsConfig() {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery<Status>({
    queryKey: ['sms-config-status'],
    queryFn: () => $.get('/admin/sms-config/status'),
    refetchInterval: 10_000, // 每 10 秒刷状态（看测试发送是否到日志）
  });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ak: '', sk: '', sign_name: '', template_verify: '', template_alert: '' });
  const [testPhone, setTestPhone] = useState('');

  const save = useMutation({
    mutationFn: () => $.post('/admin/sms-config/config', form),
    onSuccess: () => {
      message.success('SMS 配置已保存');
      setEditing(false);
      setForm({ ak: '', sk: '', sign_name: '', template_verify: '', template_alert: '' });
      qc.invalidateQueries({ queryKey: ['sms-config-status'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? '保存失败'),
  });

  const test = useMutation({
    mutationFn: () => $.post('/admin/sms-config/test-send', { phone: testPhone }),
    onSuccess: (r: any) => {
      message.success(`已发送 · 验证码预览 ${r?.sent_code_preview}`);
      qc.invalidateQueries({ queryKey: ['sms-config-status'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? '发送失败'),
  });

  if (isLoading || !status) return <div style={{ padding: 40, textAlign: 'center', color: '#8F959E' }}>加载中…</div>;

  const sourceTag = (src: 'kv' | 'env' | 'empty') => {
    if (src === 'kv') return <Tag color="blue">DB</Tag>;
    if (src === 'env') return <Tag color="gold">env</Tag>;
    return <Tag color="red">未配置</Tag>;
  };

  return (
    <div>
      {/* 状态汇总 */}
      <div style={{
        padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, marginBottom: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 500 }}>
            阿里云短信凭证
            {status.configured
              ? <Pill tone="success">就绪</Pill>
              : <Pill tone="danger">未配置</Pill>}
          </div>
          <div style={{ fontSize: 12, color: '#646A73', marginTop: 4 }}>
            优先级：DB（UI 配） &gt; env（兼容老部署）。改完无需重启，立即生效
          </div>
        </div>
        <Btn kind="primary" onClick={() => setEditing(true)}>编辑配置</Btn>
      </div>

      {/* 配置项展示 */}
      <div style={{ padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, marginBottom: 12 }}>
        <Row label="AccessKey ID" value={status.ak || '（未配置）'} source={status.sources.ak} mono />
        <Row label="AccessKey Secret" value={status.sk || '（未配置）'} source={status.sources.sk} />
        <Row label="签名 (SignName)" value={status.sign_name || '（未配置）'} source={status.sources.sign_name} />
        <Row label="验证码模板 (TemplateVerify)" value={status.template_verify || '（未配置）'} source={status.sources.template_verify} mono />
        <Row label="告警模板 (TemplateAlert)" value={status.template_alert || '（未配置）'} source={status.sources.template_alert} mono />
      </div>

      {/* 测试发送 */}
      <div style={{ padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>⚡ 测试发送</div>
        <div style={{ fontSize: 12, color: '#646A73', marginBottom: 12 }}>
          输入手机号发一条验证码（验证 AK/SK/签名/模板是否正确）。不会创建 2FA 凭证，纯链路验证。
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input placeholder="11 位手机号" value={testPhone}
                 onChange={(e) => setTestPhone(e.target.value)} style={{ maxWidth: 200 }} />
          <Btn kind="primary" onClick={() => test.mutate()}
               disabled={!status.configured || test.isPending || !testPhone}>
            {test.isPending ? '发送中…' : '发送测试'}
          </Btn>
        </div>
      </div>

      {/* 最近发送日志 */}
      <div style={{ padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>📋 最近 20 条发送记录</div>
        {(status.recent_logs ?? []).length === 0 ? (
          <div style={{ padding: 20, color: '#8F959E', fontSize: 12, textAlign: 'center' }}>暂无</div>
        ) : (
          <table style={{ width: '100%', fontSize: 12 }}>
            <thead style={{ background: '#F5F6F7', color: '#646A73' }}>
              <tr><th style={{ padding: 8, textAlign: 'left' }}>时间</th><th style={{ padding: 8, textAlign: 'left' }}>手机</th><th style={{ padding: 8, textAlign: 'left' }}>状态</th><th style={{ padding: 8, textAlign: 'left' }}>消息</th></tr>
            </thead>
            <tbody>
              {status.recent_logs.map((l, i) => (
                <tr key={i} style={{ borderTop: '1px solid #F0F2F5' }}>
                  <td style={{ padding: 8, color: '#8F959E' }}>{fmtMonthDayTime(l.at)}</td>
                  <td style={{ padding: 8, fontFamily: 'monospace' }}>{l.phone}</td>
                  <td style={{ padding: 8 }}>{l.ok ? <Tag color="green">OK</Tag> : <Tag color="red">FAIL</Tag>}</td>
                  <td style={{ padding: 8, color: '#646A73' }}>{l.msg}{l.requestId ? <span style={{ marginLeft: 4, color: '#8F959E' }}>· {l.requestId}</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 编辑 Modal */}
      <Modal open={editing} title="编辑 SMS 配置"
             onCancel={() => setEditing(false)}
             onOk={() => save.mutate()} okText="保存"
             cancelText="取消" confirmLoading={save.isPending} width={600}>
        <div style={{ fontSize: 12, color: '#646A73', marginBottom: 12 }}>
          留空表示不修改；填空字符串表示清除 KV 配置（回退到 env）
        </div>
        <FormRow label="AccessKey ID" value={form.ak} onChange={(v) => setForm({ ...form, ak: v })} placeholder="LTAI..." />
        <FormRow label="AccessKey Secret" value={form.sk} onChange={(v) => setForm({ ...form, sk: v })} placeholder="（敏感）" password />
        <FormRow label="签名 (SignName)" value={form.sign_name} onChange={(v) => setForm({ ...form, sign_name: v })} placeholder="环课" />
        <FormRow label="验证码模板 (TemplateVerify)" value={form.template_verify} onChange={(v) => setForm({ ...form, template_verify: v })} placeholder="SMS_506655210" />
        <FormRow label="告警模板 (TemplateAlert)" value={form.template_alert} onChange={(v) => setForm({ ...form, template_alert: v })} placeholder="可选" />
      </Modal>
    </div>
  );
}

function Row({ label, value, source, mono }: { label: string; value: string; source: 'kv' | 'env' | 'empty'; mono?: boolean }) {
  const sourceTag = source === 'kv' ? <Tag color="blue">DB</Tag>
    : source === 'env' ? <Tag color="gold">env</Tag>
    : <Tag color="red">未配置</Tag>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F0F2F5' }}>
      <div style={{ width: 220, fontSize: 13, color: '#646A73' }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13, fontFamily: mono ? 'monospace' : undefined }}>{value}</div>
      <div>{sourceTag}</div>
    </div>
  );
}

function FormRow({ label, value, onChange, placeholder, password }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; password?: boolean;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#646A73', marginBottom: 4 }}>{label}</div>
      {password ? (
        <Input.Password value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}
